(() => {
  'use strict';

  const root = document.querySelector('[data-void-mode]');
  const toggle = root?.querySelector('[data-void-mode-toggle]');
  const time = root?.querySelector('[data-void-mode-time]');
  const trackLibraryElement = root?.querySelector('[data-void-track-library]');
  const marquee = root?.querySelector('[data-void-mode-marquee]');
  const marqueeTexts = root?.querySelectorAll('.void-mode__marquee-text');
  const display = root?.querySelector('[data-void-mode-display]');
  const mobileTime = root?.querySelector('[data-void-mode-mobile-time]');
  const trackSelector = root?.querySelector('[data-void-track-selector]');

  if (!root || !toggle || !time) return;

  function readTrackLibrary() {
    if (!trackLibraryElement) return [];

    try {
      const tracks = JSON.parse(trackLibraryElement.textContent || '[]');
      return tracks.filter(track => (
        track &&
        typeof track.id === 'string' &&
        typeof track.audioUrl === 'string' &&
        track.audioUrl.trim() !== ''
      ));
    } catch (error) {
      return [];
    }
  }

  function selectInitialTrack(tracks) {
    if (!tracks.length) return null;

    const storageKey = 'fatcutpress:last-track-id';
    let previousTrackId = '';

    try {
      previousTrackId = window.localStorage.getItem(storageKey) || '';
    } catch (error) {}

    const candidates = tracks.length > 1
      ? tracks.filter(track => track.id !== previousTrackId)
      : tracks;
    const pool = candidates.length ? candidates : tracks;
    const selectedTrack = pool[Math.floor(Math.random() * pool.length)] || null;

    try {
      window.localStorage.setItem(storageKey, selectedTrack.id);
    } catch (error) {}

    return selectedTrack;
  }

  const tracks = readTrackLibrary();
  let currentTrack = selectInitialTrack(tracks);
  let shuffleBag = [];

  function shuffledTracks(items) {
    const shuffled = [...items];

    for (let index = shuffled.length - 1; index > 0; index -= 1) {
      const swapIndex = Math.floor(Math.random() * (index + 1));
      [shuffled[index], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[index]];
    }

    return shuffled;
  }

  function refillShuffleBag() {
    if (tracks.length <= 1) {
      shuffleBag = [];
      return;
    }

    shuffleBag = shuffledTracks(tracks);

    if (shuffleBag[0]?.id === currentTrack?.id) {
      const differentTrackIndex = shuffleBag.findIndex(track => track.id !== currentTrack.id);
      if (differentTrackIndex > 0) {
        [shuffleBag[0], shuffleBag[differentTrackIndex]] = [shuffleBag[differentTrackIndex], shuffleBag[0]];
      }
    }
  }

  function resetShuffleBagAfterSelection() {
    shuffleBag = shuffledTracks(tracks.filter(track => track.id !== currentTrack?.id));
  }

  function takeNextTrack() {
    if (tracks.length <= 1) return currentTrack;
    if (!shuffleBag.length) refillShuffleBag();
    return shuffleBag.shift() || currentTrack;
  }

  if (!currentTrack) {
    toggle.disabled = true;
    toggle.setAttribute('aria-label', 'No active audio track');
    return;
  }


  function formatTrackLabel(track) {
    const title = String(track?.title || '').trim();
    const artist = String(track?.artist || '').trim();
    if (title && artist) return `${title} — ${artist}`;
    return title || artist;
  }

  function updateTrackPresentation(track) {
    const nextLabel = formatTrackLabel(track);

    marqueeTexts?.forEach(element => {
      element.textContent = nextLabel;
    });
  }

  function emitTrackChange(track) {
    window.dispatchEvent(new CustomEvent('fatcut:track-change', {
      detail: { track }
    }));
  }

  updateTrackPresentation(currentTrack);
  emitTrackChange(currentTrack);
  resetShuffleBagAfterSelection();

  let marqueeShift = 1;
  let marqueeOffset = 0;
  let marqueeLastTime = 0;
  const MARQUEE_SPEED_PX_PER_SECOND = 12;
  const CATALOG_INITIAL_TIME_DURATION_MS = 3000;
  const CATALOG_TITLE_DURATION_MS = 7000;
  const CATALOG_TIME_DURATION_MS = 7000;
  const PLAYBACK_FEEDBACK_DURATION_MS = 720;

  function syncDesktopMarqueeMetrics() {
    if (!marquee || !marqueeTexts?.length) return;

    window.requestAnimationFrame(() => {
      const firstCopyWidth = marqueeTexts[0].scrollWidth || marqueeTexts[0].getBoundingClientRect().width;
      const gap = root.querySelector('.void-mode__marquee-gap');
      const gapWidth = gap ? (gap.getBoundingClientRect().width || 20) : 20;
      marqueeShift = Math.max(1, firstCopyWidth + gapWidth);
      root.style.setProperty('--void-marquee-shift', `${marqueeShift}px`);
      marqueeOffset %= marqueeShift;
    });
  }

  function runWhenFontsReady(callback) {
    if (!document.fonts?.ready) return;
    document.fonts.ready.then(callback).catch(() => {});
  }

  function runDesktopMarquee(now) {
    if (!marquee) return;

    if (!marqueeLastTime) marqueeLastTime = now;
    const elapsed = Math.min(64, Math.max(0, now - marqueeLastTime));
    marqueeLastTime = now;

    const isMobile = window.matchMedia('(max-width: 680px)').matches;
    const shouldRunCatalogMarquee = isCatalogMode();
    if (shouldRunCatalogMarquee && marqueeShift > 1) {
      marqueeOffset = (marqueeOffset + (MARQUEE_SPEED_PX_PER_SECOND * elapsed / 1000)) % marqueeShift;
      marquee.style.setProperty('transform', `translate3d(${-marqueeOffset}px,0,0)`, 'important');
    } else {
      marquee.style.setProperty('transform', 'translate3d(0,0,0)', 'important');
    }

    window.requestAnimationFrame(runDesktopMarquee);
  }

  syncDesktopMarqueeMetrics();
  window.requestAnimationFrame(runDesktopMarquee);
  window.addEventListener('resize', syncDesktopMarqueeMetrics, { passive: true });
  window.addEventListener('resize', syncMobileSelectorOverlayPosition, { passive: true });
  runWhenFontsReady(syncDesktopMarqueeMetrics);
  const BASE_VOLUME = 0.6;
  const LOOP_FADE_OUT_MS = 1300;
  const LOOP_FADE_IN_MS = 950;
  const LOOP_TRIGGER_SECONDS = 1.45;

  const audio = new Audio(currentTrack.audioUrl);
  audio.preload = 'auto';
  audio.loop = false;
  audio.playsInline = true;
  audio.volume = BASE_VOLUME;

  const selectorEnabled = Boolean(trackSelector && display && tracks.length >= 3);
  let selectorOpen = false;

  function storeCurrentTrack(track) {
    if (!track?.id) return;
    try {
      window.localStorage.setItem('fatcutpress:last-track-id', track.id);
    } catch (error) {}
  }

  function applyCurrentTrack(track, { resetShuffleBag = false } = {}) {
    currentTrack = track;
    if (resetShuffleBag) resetShuffleBagAfterSelection();
    updateTrackPresentation(currentTrack);
    storeCurrentTrack(currentTrack);
    renderTrackSelector();
    syncDesktopMarqueeMetrics();
    audio.src = currentTrack.audioUrl;
    audio.load();
    emitTrackChange(currentTrack);
  }

  function syncMobileSelectorOverlayPosition() {
    if (!trackSelector || !selectorEnabled) return;
    if (!window.matchMedia('(max-width: 680px)').matches) {
      root.style.removeProperty('--void-selector-screen-left');
      root.style.removeProperty('--void-selector-screen-top');
      return;
    }
    const rect = root.getBoundingClientRect();
    root.style.setProperty('--void-selector-screen-left', `${Math.max(0, Math.round(rect.left))}px`);
    root.style.setProperty('--void-selector-screen-top', `${Math.max(0, Math.round(rect.bottom))}px`);
  }

  function syncTrackSelectorMarquees() {
    syncMobileSelectorOverlayPosition();
    if (!selectorEnabled || !trackSelector) return;

    window.requestAnimationFrame(() => {
      trackSelector.querySelectorAll('.void-mode__track-label-window').forEach(windowElement => {
        const marqueeElement = windowElement.querySelector('.void-mode__track-label-marquee');
        const firstText = marqueeElement?.querySelector('.void-mode__track-label-text');
        const gap = marqueeElement?.querySelector('.void-mode__track-label-gap');
        if (!marqueeElement || !firstText || !gap) return;

        const overflow = firstText.scrollWidth > windowElement.clientWidth;
        windowElement.classList.toggle('is-overflowing', overflow);
        marqueeElement.style.setProperty('--void-track-label-shift', `${firstText.scrollWidth + gap.offsetWidth}px`);
      });
    });
  }

  function renderTrackSelector() {
    if (!selectorEnabled || !trackSelector) return;

    trackSelector.replaceChildren(...tracks.map((track, index) => {
      const button = document.createElement('button');
      const number = document.createElement('span');
      const labelWindow = document.createElement('span');
      const labelMarquee = document.createElement('span');
      const label = document.createElement('span');
      const gap = document.createElement('span');
      const repeatedLabel = document.createElement('span');
      const indicator = document.createElement('span');
      const isCurrent = track.id === currentTrack.id;
      const formattedLabel = formatTrackLabel(track);

      button.type = 'button';
      button.className = 'void-mode__track-option';
      button.dataset.voidTrackId = track.id;
      button.setAttribute('aria-current', isCurrent ? 'true' : 'false');
      button.setAttribute('aria-label', `Play track ${index + 1}: ${formattedLabel}`);

      number.className = 'void-mode__track-number';
      number.textContent = `#${String(index + 1).padStart(2, '0')}`;
      number.setAttribute('aria-hidden', 'true');

      labelWindow.className = 'void-mode__track-label-window';
      labelMarquee.className = 'void-mode__track-label-marquee';
      label.className = 'void-mode__track-label-text';
      label.textContent = formattedLabel;
      gap.className = 'void-mode__track-label-gap';
      gap.setAttribute('aria-hidden', 'true');
      repeatedLabel.className = 'void-mode__track-label-text';
      repeatedLabel.textContent = formattedLabel;
      repeatedLabel.setAttribute('aria-hidden', 'true');
      labelMarquee.append(label, gap, repeatedLabel);
      labelWindow.append(labelMarquee);

      indicator.className = 'void-mode__track-indicator';
      indicator.setAttribute('aria-hidden', 'true');

      button.append(number, labelWindow, indicator);
      return button;
    }));

    syncTrackSelectorMarquees();
  }

  function setSelectorOpen(open) {
    if (!selectorEnabled || !trackSelector || !display) return;
    selectorOpen = Boolean(open);
    root.classList.toggle('is-track-selector-open', selectorOpen);
    trackSelector.hidden = false;
    display.setAttribute('aria-expanded', String(selectorOpen));
    trackSelector.setAttribute('aria-hidden', String(!selectorOpen));
  }

  function closeTrackSelector() {
    setSelectorOpen(false);
  }

  async function selectTrack(track) {
    if (!track) {
      closeTrackSelector();
      return;
    }

    const catalogSelector = selectorEnabled && isCatalogMode();
    const mobileCatalogSelector = catalogSelector
      && window.matchMedia('(max-width: 680px)').matches;

    if (track.id === currentTrack.id) {
      closeTrackSelector();

      if (catalogSelector) {
        if (userWantsPlayback && !audio.paused) {
          userWantsPlayback = false;
          audio.pause();
          stopAudioAnalysis();
          emitAudioLevels(0, 0, false);
          clearCatalogTimers();
        } else {
          userWantsPlayback = true;
          try {
            await audio.play();
            startAudioAnalysis();
          } catch (error) {
            userWantsPlayback = false;
          }
        }

        updateRemainingTime();
        updateUI();
      }
      return;
    }

    const shouldResume = userWantsPlayback || mobileCatalogSelector;
    cancelVolumeAnimation();
    loopInProgress = false;
    audio.pause();
    applyCurrentTrack(track, { resetShuffleBag: true });
    audio.volume = BASE_VOLUME;
    closeTrackSelector();

    if (shouldResume) {
      userWantsPlayback = true;
      try {
        await audio.play();
        startAudioAnalysis();
      } catch (error) {
        userWantsPlayback = false;
      }
    }

    updateRemainingTime();
    updateUI();
  }

  if (selectorEnabled) {
    root.classList.add('has-track-selector');
    display.setAttribute('role', 'button');
    display.setAttribute('tabindex', '0');
    display.setAttribute('aria-haspopup', 'listbox');
    display.setAttribute('aria-expanded', 'false');
    trackSelector.setAttribute('role', 'listbox');
    trackSelector.setAttribute('aria-hidden', 'true');
    trackSelector.hidden = false;
    renderTrackSelector();
    window.addEventListener('resize', syncTrackSelectorMarquees, { passive: true });
    runWhenFontsReady(syncTrackSelectorMarquees);

    trackSelector.addEventListener('click', event => {
      const option = event.target.closest('[data-void-track-id]');
      if (!option) return;
      event.preventDefault();
      event.stopPropagation();
      const track = tracks.find(item => item.id === option.dataset.voidTrackId);
      selectTrack(track);
    });

    document.addEventListener('click', event => {
      if (selectorOpen && !root.contains(event.target)) closeTrackSelector();
    });

    document.addEventListener('keydown', event => {
      if (event.key === 'Escape' && selectorOpen) closeTrackSelector();
    });
  }

  let analysisFrame = 0;
  let loopInProgress = false;
  let userWantsPlayback = false;
  let volumeAnimation = 0;
  let catalogSwapTimer = 0;
  let catalogSwapTimeout = 0;
  let mobilePeekTimer = 0;
  let playbackFeedbackTimer = 0;
  let catalogInitialTimePhase = false;


  function isCatalogMode() {
    return root.classList.contains('is-catalog');
  }

  function clearCatalogTimers() {
    if (catalogSwapTimer) window.clearTimeout(catalogSwapTimer);
    if (catalogSwapTimeout) window.clearTimeout(catalogSwapTimeout);
    if (mobilePeekTimer) window.clearTimeout(mobilePeekTimer);
    catalogSwapTimer = 0;
    catalogSwapTimeout = 0;
    mobilePeekTimer = 0;
    catalogInitialTimePhase = false;
    root.classList.remove('show-catalog-time', 'is-catalog-swapping', 'is-mobile-peek');
  }

  function scheduleNextCatalogSwap() {
    if (!root.classList.contains('is-playing') || !isCatalogMode()) return;
    if (catalogSwapTimer) window.clearTimeout(catalogSwapTimer);

    const delay = catalogInitialTimePhase
      ? CATALOG_INITIAL_TIME_DURATION_MS
      : root.classList.contains('show-catalog-time')
        ? CATALOG_TIME_DURATION_MS
        : CATALOG_TITLE_DURATION_MS;

    catalogSwapTimer = window.setTimeout(() => {
      catalogSwapTimer = 0;
      catalogInitialTimePhase = false;
      swapCatalogDisplaySoftly();
    }, delay);
  }

  function swapCatalogDisplaySoftly() {
    if (!root.classList.contains('is-playing') || !isCatalogMode()) return;
    if (catalogSwapTimeout) window.clearTimeout(catalogSwapTimeout);

    if (window.matchMedia('(max-width: 680px)').matches) {
      root.classList.toggle('show-catalog-time');
      catalogSwapTimeout = window.setTimeout(() => {
        catalogSwapTimeout = 0;
        scheduleNextCatalogSwap();
      }, 240);
      return;
    }

    root.classList.remove('is-catalog-swapping');
    root.classList.toggle('show-catalog-time');

    catalogSwapTimeout = window.setTimeout(() => {
      catalogSwapTimeout = 0;
      scheduleNextCatalogSwap();
    }, 240);
  }

  function beginCatalogAlternation() {
    if (!isCatalogMode()) return;
    if (catalogSwapTimer) window.clearTimeout(catalogSwapTimer);
    if (catalogSwapTimeout) window.clearTimeout(catalogSwapTimeout);
    catalogSwapTimer = 0;
    catalogSwapTimeout = 0;
    catalogInitialTimePhase = true;
    root.classList.add('show-catalog-time');
    root.classList.remove('is-catalog-swapping');
    scheduleNextCatalogSwap();
  }

  function showMobilePeek() {
    if (!isCatalogMode() || !window.matchMedia('(max-width: 680px)').matches) return;
    if (mobilePeekTimer) window.clearTimeout(mobilePeekTimer);
    root.classList.add('is-mobile-peek');
    mobilePeekTimer = window.setTimeout(() => {
      root.classList.remove('is-mobile-peek');
      mobilePeekTimer = 0;
    }, 3200);
  }

  function triggerPlaybackFeedback() {
    if (playbackFeedbackTimer) window.clearTimeout(playbackFeedbackTimer);
    root.classList.remove('is-playback-feedback');
    void root.offsetWidth;
    root.classList.add('is-playback-feedback');
    playbackFeedbackTimer = window.setTimeout(() => {
      root.classList.remove('is-playback-feedback');
      playbackFeedbackTimer = 0;
    }, PLAYBACK_FEEDBACK_DURATION_MS);
  }

  function formatTime(seconds) {
    if (!Number.isFinite(seconds) || seconds < 0) return '--:--';
    const total = Math.max(0, Math.ceil(seconds));
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
  }

  function updateRemainingTime() {
    const remaining = Number.isFinite(audio.duration)
      ? Math.max(0, audio.duration - audio.currentTime)
      : NaN;
    const formatted = formatTime(remaining);
    const remainingLabel = isCatalogMode() && formatted !== '--:--' ? `-${formatted}` : formatted;
    time.textContent = remainingLabel;
    if (mobileTime) mobileTime.textContent = remainingLabel;
  }

  function updateUI() {
    const playing = !audio.paused && !audio.ended && userWantsPlayback;
    root.classList.toggle('is-playing', playing);
    toggle.setAttribute('aria-pressed', String(playing));
    toggle.setAttribute('aria-label', playing ? 'Pause audio' : 'Play audio');
    time.setAttribute('aria-hidden', playing ? 'false' : 'true');
    if (mobileTime) mobileTime.setAttribute('aria-hidden', playing ? 'false' : 'true');
    if (playing) updateRemainingTime();
    if (!playing) clearCatalogTimers();
  }

  function emitAudioLevels(low = 0, high = 0, playing = false) {
    window.dispatchEvent(new CustomEvent('fatcut:audio-levels', {
      detail: { low, high, playing }
    }));
  }

  function startAudioAnalysis() {
    if (analysisFrame) return;

    const analyse = () => {
      if (!userWantsPlayback || audio.paused || audio.ended) {
        analysisFrame = 0;
        emitAudioLevels(0, 0, false);
        return;
      }

      const isVoidHome = document.body.classList.contains('template-index') && document.body.dataset.hasProducts !== 'true';
      if(isVoidHome){
        const t = Number.isFinite(audio.currentTime) ? audio.currentTime : 0;
        const lowWave = 0.5 + 0.5 * Math.sin(t * 2.55);
        const lowSwell = 0.5 + 0.5 * Math.sin(t * 0.58 + 1.15);
        const lowHit = Math.pow(0.5 + 0.5 * Math.sin(t * 4.85 + 0.35), 6.0);
        const low = Math.min(1, 0.20 + lowWave * 0.34 + lowSwell * 0.18 + lowHit * 0.30);
        const highWave = 0.5 + 0.5 * Math.sin(t * 8.8 + Math.sin(t * 1.12));
        const highTick = Math.pow(0.5 + 0.5 * Math.sin(t * 13.7 + 1.8), 10.0);
        const high = Math.min(1, 0.06 + highWave * 0.20 + highTick * 0.22);
        emitAudioLevels(low, high, true);
      }else{
        emitAudioLevels(0, 0, false);
      }
      maybeBeginNaturalLoop();
      analysisFrame = requestAnimationFrame(analyse);
    };

    analysisFrame = requestAnimationFrame(analyse);
  }

  function stopAudioAnalysis() {
    if (analysisFrame) cancelAnimationFrame(analysisFrame);
    analysisFrame = 0;
    emitAudioLevels(0, 0, false);
  }

  function cancelVolumeAnimation() {
    if (volumeAnimation) cancelAnimationFrame(volumeAnimation);
    volumeAnimation = 0;
  }

  function animateVolume(from, to, duration) {
    cancelVolumeAnimation();

    return new Promise(resolve => {
      const started = performance.now();
      const step = now => {
        if (!userWantsPlayback) {
          volumeAnimation = 0;
          resolve(false);
          return;
        }
        const progress = Math.min(1, (now - started) / duration);
        const eased = progress * progress * (3 - 2 * progress);
        audio.volume = Math.max(0, Math.min(1, from + (to - from) * eased));
        if (progress < 1) {
          volumeAnimation = requestAnimationFrame(step);
        } else {
          volumeAnimation = 0;
          resolve(true);
        }
      };
      volumeAnimation = requestAnimationFrame(step);
    });
  }

  async function beginNaturalLoop() {
    if (loopInProgress || !userWantsPlayback) return;
    loopInProgress = true;

    const faded = await animateVolume(audio.volume, 0, LOOP_FADE_OUT_MS);
    if (!faded || !userWantsPlayback) {
      loopInProgress = false;
      return;
    }

    try {
      const nextTrack = takeNextTrack();

      if (nextTrack && nextTrack.id !== currentTrack.id) {
        applyCurrentTrack(nextTrack);
      } else {
        audio.currentTime = 0;
      }

      audio.volume = 0;
      await audio.play();
      await animateVolume(0, BASE_VOLUME, LOOP_FADE_IN_MS);
    } catch (error) {
      userWantsPlayback = false;
      audio.pause();
    }

    loopInProgress = false;
    updateUI();
  }

  function maybeBeginNaturalLoop() {
    if (
      userWantsPlayback &&
      !loopInProgress &&
      Number.isFinite(audio.duration) &&
      audio.duration > 0 &&
      audio.duration - audio.currentTime <= LOOP_TRIGGER_SECONDS
    ) {
      beginNaturalLoop();
    }
  }

  async function togglePlayback() {
    if (audio.paused || !userWantsPlayback) {
      triggerPlaybackFeedback();
      userWantsPlayback = true;
      try {
        audio.volume = BASE_VOLUME;
        await audio.play();
        startAudioAnalysis();
      } catch (error) {
        userWantsPlayback = false;
      }
    } else {
      userWantsPlayback = false;
      cancelVolumeAnimation();
      loopInProgress = false;
      audio.pause();
      audio.volume = BASE_VOLUME;
      stopAudioAnalysis();
      clearCatalogTimers();
    }
    updateUI();
  }

  async function fadeOut(duration = 1500){
    if(audio.paused){
      return true;
    }

    cancelVolumeAnimation();
    loopInProgress = false;

    const startVolume = Math.max(0, Math.min(1, audio.volume));
    const startedAt = performance.now();

    await new Promise(resolve => {
      const step = now => {
        const progress = Math.min(1, (now - startedAt) / Math.max(1, duration));
        /* Slow at the beginning and soft at the end. */
        const eased = progress * progress * (3 - 2 * progress);
        audio.volume = Math.max(0, startVolume * (1 - eased));

        if(progress < 1 && !audio.paused){
          volumeAnimation = requestAnimationFrame(step);
        }else{
          volumeAnimation = 0;
          resolve();
        }
      };

      volumeAnimation = requestAnimationFrame(step);
    });

    userWantsPlayback = false;
    audio.pause();
    audio.volume = BASE_VOLUME;
    stopAudioAnalysis();
    emitAudioLevels(0, 0, false);
    updateUI();
    return true;
  }

  window.FatcutAudio = { audio, fadeOut, togglePlayback };

  toggle.addEventListener('click', event => {
    event.preventDefault();
    togglePlayback();
  });

  root.addEventListener('click', event => {
    if (!isCatalogMode()) return;
    if (event.target.closest('[data-void-mode-toggle]')) return;
    if (event.target.closest('[data-void-track-selector]')) return;
    if (!event.target.closest('[data-void-mode-display]')) return;

    event.preventDefault();
    event.stopPropagation();

    if (selectorEnabled) {
      setSelectorOpen(!selectorOpen);
      return;
    }

    showMobilePeek();
  });

  display?.addEventListener('keydown', event => {
    if (!selectorEnabled || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    setSelectorOpen(!selectorOpen);
  });

  function handleTimeUpdate() {
    updateRemainingTime();
    maybeBeginNaturalLoop();
  }

  function handlePlay() {
    updateUI();
    startAudioAnalysis();
    beginCatalogAlternation();
  }

  function handleEnded() {
    if (userWantsPlayback) beginNaturalLoop();
    else updateUI();
  }

  audio.addEventListener('error', () => {
    userWantsPlayback = false;
    stopAudioAnalysis();
    updateUI();
  });

  audio.addEventListener('loadedmetadata', updateRemainingTime);
  audio.addEventListener('durationchange', updateRemainingTime);
  audio.addEventListener('timeupdate', handleTimeUpdate);
  audio.addEventListener('play', handlePlay);
  audio.addEventListener('pause', updateUI);
  audio.addEventListener('ended', handleEnded);

  function stopAndReset() {
    userWantsPlayback = false;
    cancelVolumeAnimation();
    audio.pause();
    stopAudioAnalysis();
    try { audio.currentTime = 0; } catch (error) {}
    audio.volume = BASE_VOLUME;
    clearCatalogTimers();
    if (playbackFeedbackTimer) window.clearTimeout(playbackFeedbackTimer);
    playbackFeedbackTimer = 0;
    root.classList.remove('is-playback-feedback');
    closeTrackSelector();
  }

  window.addEventListener('pagehide', stopAndReset);
  window.addEventListener('beforeunload', stopAndReset);

  updateRemainingTime();
  updateUI();
})();
